import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import Icon from '../AppIcon';
import Button from '../ui/Button';
import { DEFAULT_NOTIFICATION_AI_LANGUAGE } from '../../lib/hubupNotificationAiLanguages';
import NotificationAiLanguageSelect from './NotificationAiLanguageSelect';
import HubupAiSuggestedMediaReview from './HubupAiSuggestedMediaReview';

const URL_DESCRIPTION =
  'Indiquez l’URL de la page source sur laquelle vous voulez vous appuyer pour créer votre notification.';

export default function GenerateNotificationBrowserModal({
  open,
  loading,
  error,
  onClose,
  onSubmit,
  mediaReview = null,
  mediaReviewLoading = false,
  mediaReviewError = null,
  onMediaReviewConfirm,
  onMediaReviewSkip,
}) {
  const [urlInput, setUrlInput] = useState('');
  const [language, setLanguage] = useState(DEFAULT_NOTIFICATION_AI_LANGUAGE);

  const inMediaReview =
    !loading && mediaReview?.items && mediaReview.items.length > 0;

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (open && !loading && !inMediaReview) {
      setUrlInput('');
      setLanguage(DEFAULT_NOTIFICATION_AI_LANGUAGE);
    }
  }, [open, loading, inMediaReview]);

  const handleGenerate = () => {
    if (loading || inMediaReview) return;
    onSubmit?.({ url: urlInput, language });
  };

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[1250] flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="generate-notification-browser-title"
      aria-describedby="generate-notification-browser-desc"
      aria-busy={loading || mediaReviewLoading ? 'true' : 'false'}
    >
      <div
        className={`relative w-full rounded-xl border border-border bg-card shadow-elevation-3 overflow-hidden ${
          inMediaReview ? 'max-w-2xl' : 'max-w-lg'
        }`}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-border">
          <div className="min-w-0">
            <h2
              id="generate-notification-browser-title"
              className="text-lg font-semibold text-foreground"
            >
              {inMediaReview ? 'Valider les médias' : 'Générer une notification'}
            </h2>
            {!loading && !inMediaReview ? (
              <p
                id="generate-notification-browser-desc"
                className="text-xs text-muted-foreground mt-1 leading-relaxed"
              >
                {URL_DESCRIPTION}
              </p>
            ) : inMediaReview ? (
              <p
                id="generate-notification-browser-desc"
                className="text-xs text-muted-foreground mt-1 leading-relaxed"
              >
                Le texte est prêt. Choisissez les images ou vidéos à joindre à la
                notification.
              </p>
            ) : null}
          </div>
          <button
            type="button"
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted shrink-0"
            aria-label="Fermer"
            onClick={() => onClose?.()}
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {loading ? (
          <div className="px-6 py-10 flex flex-col items-center text-center gap-5">
            <div className="relative flex items-center justify-center w-16 h-16">
              <span className="absolute inset-0 rounded-full border-2 border-primary/20" />
              <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-primary animate-spin" />
              <Icon name="Loader2" size={28} className="text-primary animate-spin relative z-[1]" />
            </div>
            <div className="space-y-2 max-w-sm">
              <p className="text-sm font-medium text-foreground">
                Préparation de votre notification…
              </p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Cela peut prendre un moment. Si vous quittez cette page, la notification
                sera enregistrée automatiquement dans vos{' '}
                <span className="font-medium text-foreground">brouillons</span>.
              </p>
            </div>
          </div>
        ) : inMediaReview ? (
          <div className="p-5">
            <HubupAiSuggestedMediaReview
              items={mediaReview.items}
              loading={mediaReviewLoading}
              error={mediaReviewError}
              onConfirm={(urls) => onMediaReviewConfirm?.(urls)}
              onSkip={() => onMediaReviewSkip?.()}
            />
          </div>
        ) : (
          <div className="p-5 space-y-4">
            <div>
              <label
                htmlFor="hubup-generate-language"
                className="text-xs font-medium text-foreground block mb-1.5"
              >
                Langue à utiliser pour générer la notification
              </label>
              <NotificationAiLanguageSelect
                id="hubup-generate-language"
                value={language}
                onChange={setLanguage}
              />
            </div>

            <div>
              <label
                htmlFor="hubup-generate-url"
                className="text-xs font-medium text-foreground block mb-1.5"
              >
                URL de la page
              </label>
              <input
                id="hubup-generate-url"
                type="url"
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                placeholder="https://…"
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleGenerate();
                }}
                autoComplete="url"
              />
            </div>

            {error ? (
              <p className="text-sm text-error" role="alert">
                {error}
              </p>
            ) : null}

            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
              <Button type="button" variant="outline" onClick={() => onClose?.()}>
                Annuler
              </Button>
              <Button type="button" onClick={handleGenerate}>
                Générer
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
